// client/src/pages/QuizPlayer/index.jsx - תיקון לוגיקת התשובות
import { useMemo, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthState, useAuth } from "../../context/AuthContext";
import { useApp } from "../../context/AppContext";
import customFetch from "../../api/customFetch";
import ProgressBadge from "../../components/ProgressBadge";
import QuestionCard from "../../components/QuestionCard";

// Loading component
const LoadingScreen = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      background: "var(--bg)",
      color: "var(--text)",
    }}
  >
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: "1.2rem", marginBottom: "10px" }}>טוען...</div>
      <div style={{ color: "var(--text)" }}>מכין את הבחינה</div>
    </div>
  </div>
);

export default function QuizPlayer() {
  const { state, dispatch } = useApp();
  const { slug } = useParams();
  const nav = useNavigate();
  const { isLoggedIn, user, loading } = useAuthState();
  const { logout } = useAuth();
  const [creatingAttempt, setCreatingAttempt] = useState(false);

  const q = useMemo(
    () => state.course?.questions?.[state.currentIndex],
    [state]
  );

  useEffect(() => {
    if (loading) return;

    if (!isLoggedIn) {
      nav("/user/login");
      return;
    }

    // טען קורס אם נכנסו ישירות לשאלון
    (async () => {
      try {
        if (!state.course) {
          const { data } = await customFetch.get(`/courses/${slug}`);
          dispatch({ type: "LOAD_COURSE", payload: data });
        }
      } catch (error) {
        console.error("Error loading course:", error);
        if (error.response?.status === 401) {
          await logout();
          nav("/user/login");
        }
      }
    })();
  }, [slug, state.course, dispatch, nav, isLoggedIn, loading, logout]);

  // יצירת attempt אוטומטית אם לא קיים
  useEffect(() => {
    if (!state.course || state.attemptId || creatingAttempt) return;

    const createAttempt = async () => {
      try {
        setCreatingAttempt(true);

        // יצירת attempt פשוט במידת הצורך
        const attemptId = `attempt_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        dispatch({ type: "SET_ATTEMPT", payload: attemptId });
      } catch (error) {
        console.error("Error creating attempt:", error);
      } finally {
        setCreatingAttempt(false);
      }
    };

    createAttempt();
  }, [state.course, state.attemptId, creatingAttempt, dispatch]);

  const handleAnswer = async (label) => {
    try {
      // חישוב האם התשובה נכונה
      const selectedAnswer = q?.answers?.find((a) => a.label === label);
      const isCorrect = selectedAnswer?.isCorrect || false;
      const explain = selectedAnswer?.explain || "";

      console.log("Answer selected:", { label, isCorrect, selectedAnswer });

      // שמירת התשובה בשרת
      const { data } = await customFetch.post(`/progress/${slug}/answer`, {
        questionIndex: state.currentIndex,
        chosenLabel: label,
        isCorrect: isCorrect,
      });

      console.log("Server response:", data);

      // עדכון המצב עם התשובה הנכונה
      dispatch({
        type: "SUBMIT_ANSWER",
        payload: {
          index: state.currentIndex,
          chosenLabel: label,
          isCorrect: isCorrect, // משתמש בחישוב המקומי
          explain: explain,
        },
      });
    } catch (error) {
      console.error("Error submitting answer:", error);
      if (error.response?.status === 401) {
        await logout();
        nav("/user/login");
      }
    }
  };

  const next = async () => {
    if (state.currentIndex < state.total - 1) {
      return dispatch({ type: "NEXT" });
    }

    // שאלה אחרונה -> סיום בשרת
    try {
      const { data } = await customFetch.post(`/progress/${slug}/complete`);
      dispatch({ type: "RESET_QUIZ" });
      nav(
        `/${slug}/results?score=${data.results.score}&passed=${data.results.passed}&correct=${data.results.correctAnswers}&total=${data.results.totalQuestions}`
      );
    } catch (error) {
      console.error("Error finishing quiz:", error);
      // אם אין endpoint לסיום, נווט ישירות לתוצאות
      const correctAnswers = Object.values(state.answers).filter(
        (answer) => answer.isCorrect
      ).length;
      const score = Math.round((correctAnswers / state.total) * 100);
      dispatch({ type: "RESET_QUIZ" }); // איפוס לפני ניווט
      nav(
        `/${slug}/results?score=${score}&passed=${
          score >= 70
        }&correct=${correctAnswers}&total=${state.total}`
      );
    }
  };

  if (loading || creatingAttempt) {
    return <LoadingScreen />;
  }

  if (!isLoggedIn) {
    return null;
  }

  // אם אין נתוני בחינה
  if (!state.course || !q) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          direction: "rtl",
          background: "var(--bg)",
        }}
      >
        <div
          style={{
            background: "var(--card)",
            padding: "40px",
            borderRadius: "16px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
            textAlign: "center",
          }}
        >
          <h3 style={{ color: "#ffffff", marginTop: 0 }}>
            שגיאה בטעינת הבחינה
          </h3>
          <p style={{ color: "var(--text)" }}>לא נמצאו שאלות לקורס זה.</p>
          <button
            onClick={() => nav(`/${slug}`)}
            className="btn"
            style={{
              marginLeft: "10px",
            }}
          >
            התחל מחדש
          </button>
          <button
            onClick={() => nav("/user/dashboard")}
            style={{
              background: "var(--text)",
              color: "var(--bg)",
              border: "none",
              padding: "10px 16px",
              borderRadius: "16px",
              cursor: "pointer",
              fontWeight: "700",
            }}
          >
            חזור לדשבורד
          </button>
        </div>
      </div>
    );
  }

  const chosen = state.answers[state.currentIndex];

  return (
    <div className="page" dir="rtl">
      {/* הצגת פרטי המשתמש */}
      <div
        style={{
          background: "var(--card)",
          padding: "16px 20px",
          borderRadius: "16px",
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div>
          <div style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
            {state.course?.title} - בחינה
          </div>
          <div style={{ fontSize: "0.9rem"}}>
            לומד: {user?.fullName || user?.username}
          </div>
        </div>
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: "0.9rem" }}>
            התקדמות כללית:
          </div>
          <div
            style={{
              fontSize: "1.1rem",
              fontWeight: "bold",
              // color: "var(--accent)",
            }}
          >
            {Math.round(
              ((state.currentIndex + (chosen ? 1 : 0)) / state.total) * 100
            )}
            %
          </div>
        </div>
      </div>

      <ProgressBadge />
      <QuestionCard q={q} onAnswer={handleAnswer} chosen={chosen} />

      <div style={{ marginTop: 18 }}>
        <button
          className="btn"
          disabled={!chosen}
          onClick={next}
          style={{
            background: !chosen ? "var(--text)" : "var(--accent)",
            color: !chosen ? "var(--bg)" : "#0b1c2b",
            opacity: !chosen ? 0.6 : 1,
            marginLeft: "10px",
          }}
        >
          {state.currentIndex < state.total - 1 ? "שאלה הבאה" : "סיום הבחינה"}
          {!chosen ? " (בחר תשובה)" : ""}
        </button>

        {/* <button
          onClick={() => nav("/user/dashboard")}
          style={{
            background: "#ff4757",
            color: "white",
            border: "none",
            padding: "10px 16px",
            borderRadius: "16px",
            cursor: "pointer",
            fontSize: "1rem",
            fontWeight: "700",
          }}
        >
          יציאה מהבחינה
        </button> */}
      </div>

      {/* התראה לפני יציאה */}
      <div
        style={{
          marginTop: "20px",
          padding: "16px",
          background: "var(--card)",
          border: "1px solid var(--accent)",
          borderRadius: "16px",
          fontSize: "0.9rem",
          color: "var(--text)",
        }}
      >
        ההתקדמות שלך נשמרת אוטומטית. תוכל לחזור ולהמשיך מהדשבורד.
      </div>

      <button
        onClick={() => nav("/user/dashboard")}
        style={{
          background: "#ff4757",
          marginTop: "20px",
          color: "white",
          border: "none",
          padding: "10px 16px",
          borderRadius: "16px",
          cursor: "pointer",
          fontSize: "1rem",
          fontWeight: "700",
        }}
      >
        יציאה מהבחינה
      </button>
    </div>
  );
}
